/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDeleteProfile, useUpdateProfile } from '../../hooks/useProfile';

import { usePopupsContext } from '../../contexts/PopupsContext';
import BasicButton from '../buttons/BasicButton';
import FormButton from '../buttons/FormButton';
import DateField from '../fields/DateField';
import TextField from '../fields/TextField';
import FormStatus from '../partials/FormStatus';
import DeleteAccountConfirmModal from '../popups/DeleteAccountConfirmModal';
import FormControl from './FormControl';
import FormRow from './FormRow';

import styles from '../../styles/forms/profile-form.module.css';

const propTypes = {
    action: PropTypes.string.isRequired,
    withDelete: PropTypes.bool,
    className: PropTypes.string,
    onComplete: PropTypes.func,
};

const defaultProps = {
    withDelete: false,
    className: null,
    onComplete: null,
};

function ProfileForm({ action, withDelete, className, onComplete }) {
    const user = useUser();
    const tracking = useTracking();
    const { updateProfileAsync } = useUpdateProfile();
    const { deleteProfileAsync } = useDeleteProfile();
    const formFields = useMemo(
        () => ['firstname', 'lastname', 'postalcode', 'birthdate'].filter(Boolean),
        [],
    );

    const now = useMemo(() => new Date().toISOString().split('T')[0], []);

    const initialValue = useMemo(() => pick(user, formFields), [user, formFields]);

    const onFormComplete = useCallback(() => {
        if (onComplete !== null) {
            onComplete();
        }
    }, [onComplete]);

    const { fields, onSubmit, status, errors } = useForm({
        fields: formFields,
        initialValue,
        postForm: (formAction, data) => updateProfileAsync(data),
        onComplete: onFormComplete,
    });

    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const onClickDelete = useCallback(() => setDeleteConfirm(true), []);
    const onDeleteConfirmClosed = useCallback(() => setDeleteConfirm(false), []);
    const { addMessage } = usePopupsContext();
    const onDeleteConfirmed = useCallback(() => {
        setDeleteConfirm(false);
        deleteProfileAsync().then(() => {
            addMessage('profile_deleted');
            tracking.trackEvent('profile', 'delete');
        });
    }, [deleteProfileAsync, tracking]);

    return (
        <form
            action={action}
            method="post"
            onSubmit={onSubmit}
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                },
                className,
            ])}
        >
            <FormRow className={styles.formRow}>
                <FormControl
                    label={<FormattedMessage defaultMessage="Prénom" description="Field label" />}
                    className={classNames([styles.formControl, styles.firstName])}
                    {...fields.firstname}
                    withoutErrors
                >
                    <TextField withBorder {...fields.firstname} className={styles.field} />
                </FormControl>
                <FormControl
                    label={<FormattedMessage defaultMessage="Nom" description="Field label" />}
                    className={styles.formControl}
                    {...fields.lastname}
                    withoutErrors
                >
                    <TextField withBorder {...fields.lastname} className={styles.field} />
                </FormControl>
            </FormRow>
            <FormRow alwaysHorizontal className={styles.formRow}>
                <FormControl
                    label={
                        <FormattedMessage defaultMessage="Code postal" description="Field label" />
                    }
                    className={styles.formControl}
                    {...fields.postalcode}
                    withoutErrors
                >
                    <TextField withBorder {...fields.postalcode} className={styles.field} />
                </FormControl>
                <FormControl
                    label={
                        <FormattedMessage
                            defaultMessage="Date de naissance"
                            description="Field label"
                        />
                    }
                    className={styles.formControl}
                    {...fields.birthdate}
                    withoutErrors
                >
                    <DateField
                        withBorder
                        {...fields.birthdate}
                        min="1900-01-01"
                        max={now}
                        className={styles.field}
                    />
                </FormControl>
            </FormRow>
            <div className={styles.actions}>
                {withDelete ? (
                    <BasicButton onClick={onClickDelete} className={styles.deleteButton}>
                        <FormattedMessage
                            defaultMessage="Supprimer le compte"
                            description="Button label"
                        />
                    </BasicButton>
                ) : null}
                <FormStatus status={status} className={styles.status} />
                <FormButton type="submit" className={styles.button} disabled={status === 'loading'}>
                    <FormattedMessage defaultMessage="Enregistrer" description="Button label" />
                </FormButton>
            </div>
            {deleteConfirm ? (
                <DeleteAccountConfirmModal
                    onClosed={onDeleteConfirmClosed}
                    onConfirmed={onDeleteConfirmed}
                />
            ) : null}
        </form>
    );
}

ProfileForm.propTypes = propTypes;
ProfileForm.defaultProps = defaultProps;

export default ProfileForm;
